import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/** A single navigation entry rendered in the responsive sidebar. */
export interface SidebarNavItem {
  /** Visible label. */
  label: string;
  /** Icon image source (e.g. `/assets/admin/icons8-...png`). */
  icon: string;
  /** Internal route — rendered as `routerLink`. Mutually exclusive with `externalUrl`. */
  link?: string;
  /** External URL — rendered as an `<a target="_blank">`. Mutually exclusive with `link`. */
  externalUrl?: string;
  /** Use exact path matching for the active state (default is prefix matching). */
  exact?: boolean;
  /** Render a divider line above this item. */
  dividerBefore?: boolean;
}

/**
 * Responsive admin/host shell with a sidebar that is a static column on desktop
 * and an off-canvas drawer on mobile — driven purely by CSS media queries.
 *
 * No viewport/device detection (`isDesktop`) is involved: the only runtime state is
 * whether the mobile drawer is open, which is reset automatically on navigation.
 */
@Component({
  selector: 'side-nav-layout',
  templateUrl: './sidebar-layout.component.html',
  styleUrls: ['./sidebar-layout.component.scss'],
})
export class SidebarLayoutComponent implements OnInit, OnDestroy {
  /** Brand logo link (the public web domain). */
  @Input() homeUrl = '';
  /** Send the logo to `rootLink` instead of the public web domain. */
  @Input() logoToRoot = false;
  /** Brand label route (e.g. `/host`, `/admin`). */
  @Input() rootLink = '';
  /** Brand label text (e.g. `파트너 센터`, `관리자`). */
  @Input() label = '';
  /** Navigation items rendered in the sidebar. */
  @Input() menu: SidebarNavItem[] = [];

  /** Whether the mobile drawer is open. Has no visual effect on desktop. */
  open = false;

  // Exact match so e.g. /host/schedule and /host/schedule/daily don't both light up.
  readonly exactMatch: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };
  readonly prefixMatch: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  private destroy$ = new Subject<void>();

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Close the drawer after navigating (mobile); harmless on desktop.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => (this.open = false));
  }

  toggle(): void {
    this.open = !this.open;
  }

  close(): void {
    this.open = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
